import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { razorpayService } from '../services/razorpayService';
import logo from '../assets/logo.png';

const NewRegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentMode, setPaymentMode] = useState('demo'); // 'demo' or 'live'

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    
    // Check if demo mode or live payment
    if (paymentMode === 'demo') {
      // Demo mode - free registration
      try {
        const registrationData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          payment_id: 'demo_payment_' + Date.now(),
          order_id: 'demo_order_' + Date.now()
        };

        await authService.register(registrationData);
        setSuccess('Registration successful! You can now login with your credentials.');
        
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        
      } catch (err) {
        setError(err.response?.data?.message || 'Registration failed');
      } finally {
        setLoading(false);
      }
    } else {
      // Live payment mode - Razorpay integration
      setLoading(false);
      razorpayService.openRegistrationPayment(
        formData,
        async (verificationResult, razorpayResponse) => {
          // Payment successful - now register user
          setLoading(true);
          try {
            const registrationData = {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              password: formData.password,
              payment_id: razorpayResponse.razorpay_payment_id,
              order_id: razorpayResponse.razorpay_order_id
            };

            await authService.register(registrationData);
            setSuccess('Payment successful! Registration complete. Redirecting to login...');
            
            setTimeout(() => {
              navigate('/login');
            }, 2000);
            
          } catch (err) {
            setError(err.response?.data?.message || 'Registration failed after payment');
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          // Payment failed
          setError(error.message || 'Payment failed. Please try again.');
          setLoading(false);
        }
      );
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 3,
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="TICSPL" style={{ height: '40px', marginRight: '12px' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            TEAM INDUSTRIAL CONSULTANCY SERVICES
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            sx={{ borderRadius: 2 }}
          >
            Register
          </Button>
          <Button 
            variant="contained" 
            onClick={() => navigate('/login')}
            sx={{ 
              bgcolor: '#1e40af', 
              borderRadius: 2,
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', px: 12, minHeight: '70vh', pt: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 6 }}>
          {/* Left Side - Content */}
          <Box sx={{ flex: 1, maxWidth: '45%' }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                color: '#1e293b', 
                mb: 2,
                fontSize: { xs: '2rem', md: '2.8rem' }
              }}
            >
              Join TICSPL Today
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#64748b', 
                mb: 3, 
                lineHeight: 1.4,
                fontSize: '1rem',
                maxWidth: '400px'
              }}
            >
              Start your industrial establishment journey with expert guidance and comprehensive support from our experienced team.
            </Typography>
            
            {/* Registration Benefits */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#1e293b' }}>
                What you get:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {[
                  'Expert consultation for industrial projects',
                  'Access to government schemes and subsidies',
                  'Document preparation and submission support',
                  'Project tracking and progress monitoring',
                  'Dedicated consultant assignment'
                ].map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ color: '#10b981', mr: 1, fontSize: '0.9rem' }}>✓</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>{benefit}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ 
              p: 2, 
              bgcolor: '#fef3c7', 
              borderRadius: 2, 
              border: '1px solid #f59e0b' 
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#92400e', mb: 0.5 }}>
                Demo Mode: Free Registration
              </Typography>
              <Typography variant="body2" color="#92400e" sx={{ fontSize: '0.85rem' }}>
                Registration is currently free for demo purposes.
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Registration Form */}
          <Box sx={{ flex: 1, maxWidth: '55%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Card sx={{ maxWidth: 450, boxShadow: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                  Create Account
                </Typography>
                
                {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>{success}</Alert>}
                
                <Box component="form" onSubmit={handleRegister}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    label="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  
                  <TextField
                    fullWidth
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 1.5 }}
                    helperText="Minimum 6 characters"
                  />
                  
                  <TextField
                    fullWidth
                    name="confirmPassword"
                    type="password"
                    label="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Payment Mode Selection */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Registration Mode
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        fullWidth
                        variant={paymentMode === 'demo' ? 'contained' : 'outlined'}
                        onClick={() => setPaymentMode('demo')}
                        sx={{ 
                          py: 1,
                          bgcolor: paymentMode === 'demo' ? '#10b981' : 'transparent',
                          '&:hover': { bgcolor: paymentMode === 'demo' ? '#059669' : 'rgba(16, 185, 129, 0.1)' }
                        }}
                      >
                        Demo (Free)
                      </Button>
                      <Button
                        fullWidth
                        variant={paymentMode === 'live' ? 'contained' : 'outlined'}
                        onClick={() => setPaymentMode('live')}
                        sx={{ 
                          py: 1,
                          bgcolor: paymentMode === 'live' ? '#1e40af' : 'transparent',
                          '&:hover': { bgcolor: paymentMode === 'live' ? '#1d4ed8' : 'rgba(30, 64, 175, 0.1)' }
                        }}
                      >
                        Live (₹99)
                      </Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: paymentMode === 'demo' ? '#f0fdf4' : '#eff6ff', 
                    borderRadius: 1, 
                    mb: 2,
                    textAlign: 'center',
                    border: `1px solid ${paymentMode === 'demo' ? '#86efac' : '#93c5fd'}`
                  }}>
                    {paymentMode === 'demo' ? (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#10b981' }}>
                          Demo Mode: Free Registration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          No payment required for testing
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e40af' }}>
                          Live Mode: ₹99 Registration Fee
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Secure payment via Razorpay
                        </Typography>
                      </>
                    )}
                  </Box>
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: paymentMode === 'demo' ? '#10b981' : '#1e40af',
                      py: 1.2,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      mb: 1.5,
                      '&:hover': { bgcolor: paymentMode === 'demo' ? '#059669' : '#1d4ed8' }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : paymentMode === 'demo' ? (
                      'Register (Free for Demo)'
                    ) : (
                      'Pay ₹99 & Register'
                    )}
                  </Button>
                  
                  <Typography variant="body2" sx={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                    Already have an account?{' '}
                    <Link 
                      component="button" 
                      type="button" 
                      onClick={() => navigate('/login')}
                      sx={{ color: '#1e40af', fontWeight: 600 }}
                    >
                      Login
                    </Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewRegisterPage;